// One period for every tab of „Wynik firmy” (PR2, 2026-10-29): a year and a
// range in it (the whole year, a quarter or a month). It lives in the page
// address, so a tab change, a reload or a copied link keep it:
//   `year=2027`           — another year than the current one (left out otherwise);
//   `period=q1` … `q4`    — a quarter of that year;
//   `month=2026-10`       — one month (carries its year; a month bar of the Pulpit).
// Former links keep working: `period=2025` (a year from a month bar),
// `period=thisYear|lastYear|twoYears` (the old „Sprzedaż i zamówienia” choice).
import type { Dayjs } from 'dayjs';

export type ReportRangeKind = 'year' | 'quarter' | 'month';

export interface ReportPeriod {
  year: number;
  kind: ReportRangeKind;
  /** 1–4 for a quarter. */
  quarter: number | null;
  /** 1–12 for a month. */
  month: number | null;
}

const pad = (value: number) => String(value).padStart(2, '0');

export function readReportPeriod(params: URLSearchParams, now: Dayjs): ReportPeriod {
  const current = now.year();
  const month = params.get('month');
  const monthMatch = month ? /^(\d{4})-(\d{2})$/.exec(month) : null;
  if (monthMatch && Number(monthMatch[2]) >= 1 && Number(monthMatch[2]) <= 12) {
    return { year: Number(monthMatch[1]), kind: 'month', quarter: null, month: Number(monthMatch[2]) };
  }
  const yearParam = params.get('year');
  const period = params.get('period');
  let year = yearParam && /^\d{4}$/.test(yearParam) ? Number(yearParam) : current;
  if (period && /^\d{4}$/.test(period)) year = Number(period);
  else if (period === 'lastYear') year = current - 1;
  else if (period === 'thisYear' || period === 'twoYears') year = current;
  const quarter = period && /^q[1-4]$/.test(period) ? Number(period.slice(1)) : null;
  return quarter ? { year, kind: 'quarter', quarter, month: null } : { year, kind: 'year', quarter: null, month: null };
}

/** A copy of `params` with the period written in (the current year is left out). */
export function writeReportPeriod(params: URLSearchParams, period: ReportPeriod, now: Dayjs): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('period');
  next.delete('month');
  next.delete('year');
  if (period.kind === 'month' && period.month) {
    next.set('month', `${period.year}-${pad(period.month)}`);
    return next;
  }
  if (period.year !== now.year()) next.set('year', String(period.year));
  if (period.kind === 'quarter' && period.quarter) next.set('period', `q${period.quarter}`);
  return next;
}

/** Month indexes (0–11) of the period in its year. */
export function reportPeriodIndexes(period: ReportPeriod): number[] {
  if (period.kind === 'month' && period.month) return [period.month - 1];
  if (period.kind === 'quarter' && period.quarter) {
    const first = (period.quarter - 1) * 3;
    return [first, first + 1, first + 2];
  }
  return Array.from({ length: 12 }, (_, index) => index);
}

/** The period's months as `YYYY-MM`. */
export function reportPeriodMonths(period: ReportPeriod): string[] {
  return reportPeriodIndexes(period).map((index) => `${period.year}-${pad(index + 1)}`);
}

export function reportPeriodRange(period: ReportPeriod): { from: string; to: string } {
  const months = reportPeriodMonths(period);
  return { from: months[0], to: months[months.length - 1] };
}

/** Value of the range select: `year`, `q1`…`q4`, `m01`…`m12`. */
export function reportRangeValue(period: ReportPeriod): string {
  if (period.kind === 'month' && period.month) return `m${pad(period.month)}`;
  if (period.kind === 'quarter' && period.quarter) return `q${period.quarter}`;
  return 'year';
}

export function periodFromRangeValue(year: number, value: string): ReportPeriod {
  if (/^q[1-4]$/.test(value)) return { year, kind: 'quarter', quarter: Number(value.slice(1)), month: null };
  if (/^m\d{2}$/.test(value)) return { year, kind: 'month', quarter: null, month: Number(value.slice(1)) };
  return { year, kind: 'year', quarter: null, month: null };
}

export function isWholeYear(period: ReportPeriod): boolean {
  return period.kind === 'year';
}

const ROMAN = ['I', 'II', 'III', 'IV'];

/**
 * Name of a month in the language: „Październik” as a select label
 * (`capital`), „październik” inside a sentence („za październik 2026”).
 */
export function monthLongName(month: number, language: string, capital = true): string {
  const name = new Intl.DateTimeFormat(language.startsWith('pl') ? 'pl-PL' : language, { month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2000, month - 1, 1)));
  return capital ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/** Roman number of a quarter („III”). */
export function quarterRoman(quarter: number): string {
  return ROMAN[quarter - 1] ?? String(quarter);
}
